import {
  InsureIQAppLogoCompactUrl,
  InsureIQAppLogoUrl,
} from '@harvestiq/constants';
import { isDevelopmentNodeEnv } from './environment';
import { getBool, getNumber } from './utils';

export * from './auth';
export * from './databases';
export * from './email';
export * from './environment';
export * from './insurance';
export * from './lambda';
export * from './logging';
export * from './redis';
export * from './sms';
export * from './trading';

// LOCALHOST
export function getFrontendHost(): string {
  return process.env['FRONTEND_HOST'] || 'http://localhost:4470';
}

export function getBackendHost(): string {
  return process.env['BACKEND_HOST'] || 'http://localhost:4471';
}

export function getPort(): number {
  return Number(process.env['PORT']) || 4471;
}

export function getWorkerPort(): number {
  return Number(process.env['WORKER_PORT']) || 9001;
}

export function getHost(): string {
  return process.env['HOST'] || '0.0.0.0';
}

// BARCHART
export function getBarchartDelayedApiToken(): string {
  return process.env['BARCHART_API_TOKEN'] || '';
}

export function getBarchartRealtimeApiToken(): string {
  return process.env['BARCHART_REALTIME_API_TOKEN'] || '';
}

// BOLDSIGN
export function getBoldsignApiKey(): string {
  return process.env['BOLDSIGN_API_KEY'] || '';
}

// HYBISCUS
export function getHybiscusApiKey(): string {
  return process.env['HYBISCUS_API_KEY'] || '';
}

export function getS3BucketForReports(): string {
  return process.env['AWS_REPORTS_BUCKET'] || 'reports-us-east-2-staging1';
}

export function getS3BucketForUploads(): string {
  return process.env['AWS_UPLOADS_BUCKET'] || 'uploads-us-east-2-staging1';
}

// GEOCODIO
export function getGeocodioApiKey(): string {
  return process.env['GEOCODIO_API_KEY'] || '';
}

// FMH
export const getFmhOrigin = () => {
  return process.env['FMH_ORIGIN'];
};

// AgWorks
export const getAgWorksOrigin = () => {
  return process.env['AGWORKS_ORIGIN'];
};

// InsureIQ API
export const getInsureiqApiHost = () => {
  return process.env['INSUREIQ_API_HOST'] || process.env['SGPORTAL_API_HOST'];
};

export const getInsureiqClientId = () => {
  return process.env['INSUREIQ_CLIENT_ID'] || process.env['SGPORTAL_CLIENT_ID'];
};

export function getInsureiqClientSecret(): string | undefined {
  return (
    process.env['INSUREIQ_CLIENT_SECRET'] ||
    process.env['SGPORTAL_CLIENT_SECRET']
  );
}

// Crypto
export function getInsureIQCryptoIV(): string | undefined {
  return process.env['CRYPTO_IV'] || process.env['PORTAL_CRYPTO_IV'];
}

export function getInsureIQCryptoSecret(): string | undefined {
  return process.env['CRYPTO_SECRET'] || process.env['PORTAL_CRYPTO_SECRET'];
}

export function getInsureIQSourceCryptoIV(): string | undefined {
  return process.env['SOURCE_CRYPTO_IV'];
}

export function getInsureIQSourceCryptoSecret(): string | undefined {
  return process.env['SOURCE_CRYPTO_SECRET'];
}

// IIQ Service Account API Key, used in DB reset script
export function getInsureIQServiceAccountApiKeyClientId(): string {
  return process.env['IIQ_SERVICE_ACCOUNT_API_KEY_CLIENT_ID'] || '';
}
export function getInsureIQServiceAccountApiKeyClientSecret(): string {
  return process.env['IIQ_SERVICE_ACCOUNT_API_KEY_CLIENT_SECRET'] || '';
}

// Contracts
export function getS3FolderForLighthouseContractsFiles(): string | undefined {
  return process.env['S3_FOLDER_FOR_LIGHTHOUSE_CONTRACTS_FILES'];
}

// Test environment detection
export function isTestRunner(): boolean {
  return !!process.env['JEST_WORKER_ID'] || !!process.env['VITEST_POOL_ID'];
}

// Local E2E test environment detection
export function isE2ERunner(): boolean {
  return getBool('IS_E2E', false);
}

// Ash Cap sFTP
export function getAshCapSftpHost(): string {
  return process.env['ASH_CAP_SFTP_HOST'] || 'sftp.ashcapitalmgmt.com';
}
export function getAshCapSftpPort(): number {
  return Number(process.env['ASH_CAP_SFTP_PORT']) || 22;
}
export function getAshCapSftpUsername(): string | undefined {
  return process.env['ASH_CAP_SFTP_USERNAME'];
}
export function getAshCapSftpPassword(): string | undefined {
  return process.env['ASH_CAP_SFTP_PASSWORD'];
}

export function getInsureIQDefaultS3Bucket(): string {
  return process.env['DEFAULT_S3_BUCKET'] || 'portal-media-beta';
}

export function getInsureIQAccessKeyId(): string | undefined {
  return (
    process.env['PORTAL_SG_AWS_ACCESS_KEY_ID'] ||
    process.env['SG_AWS_ACCESS_KEY_ID']
  );
}

export function getInsureIQSecretAccessKey(): string | undefined {
  return (
    process.env['PORTAL_SG_AWS_SECRET_ACCESS_KEY'] ||
    process.env['SG_AWS_SECRET_ACCESS_KEY']
  );
}

// InsureIQ
export function getInsureIQIsDebugXlsxEnabled(): boolean {
  return isDevelopmentNodeEnv() && !!process.env['DEBUG_GENERATE_XLSX'];
}

export function isAssignInsureIQAdminBasedOnEmailEnabled(): boolean {
  return getBool('ENABLE_STOCKGUARD_ADMIN', isDevelopmentNodeEnv());
}

export function getInsureIqAuthServiceUrl(): string {
  return process.env['AUTH_SERVICE_URL'] || 'http://localhost:8080';
}

export function getInsureIqCdnUrl(): string {
  return process.env['CDN_URL'] || 'https://cdn.harvestiq.com';
}

export function getInsureIqClientPort(): number {
  return getNumber('CLIENT_PORT', 4472);
}

export function getInsureIqReactAppServerPort(): number | null {
  const port = getNumber('REACT_APP_SERVER_PORT');
  if (port !== 0) {
    return port;
  }
  return null;
}

export function getInsureIqServerPort(): number {
  return (
    getInsureIqReactAppServerPort() ??
    getNumber(
      'SERVER_PORT',
      isDevelopmentNodeEnv() ? 4473 : getInsureIqClientPort()
    )
  );
}

export function getInsureIqAppName(): string {
  return process.env['APP_NAME'] || 'HarvestIQ';
}

export function getInsureIqAppLogo(): string {
  return process.env['APP_LOGO'] || InsureIQAppLogoUrl;
}

export function getInsureIqAppLogoCompact(): string {
  return process.env['APP_LOGO_COMPACT'] || InsureIQAppLogoCompactUrl;
}
